import { useState, useCallback } from 'react';

const SESSION_PREFIX = 'daater:dashboard:';

function readSession<T>(key: string): T | undefined {
  try {
    const raw = sessionStorage.getItem(SESSION_PREFIX + key);
    if (raw === null) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function writeSession<T>(key: string, value: T): void {
  try {
    sessionStorage.setItem(SESSION_PREFIX + key, JSON.stringify(value));
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}

export function useSessionState<T>(
  key: string,
  fallback: T | (() => T)
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setStateRaw] = useState<T>(() => {
    const stored = readSession<T>(key);
    if (stored !== undefined) return stored;
    return typeof fallback === 'function' ? (fallback as () => T)() : fallback;
  });

  const setState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStateRaw(prev => {
        const next = typeof value === 'function' ? (value as (prev: T) => T)(prev) : value;
        writeSession(key, next);
        return next;
      });
    },
    [key]
  );

  return [state, setState];
}

export function clearDashboardSession(): void {
  const toRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k?.startsWith(SESSION_PREFIX)) toRemove.push(k);
  }
  toRemove.forEach(k => sessionStorage.removeItem(k));
}
